import moment from "moment";

/**
 * Helper function to parse time strings (HH:mm:ss or HH:mm) to numeric hours
 * @param {string} timeString - Time in format HH:mm:ss or HH:mm
 * @returns {number} Time as decimal hours (e.g., 9.5 for 09:30)
 */
export const parseTimeToHours = (timeString) => {
  if (!timeString) return null;
  const [hours, minutes] = timeString
    .split(":")
    .map((num) => parseInt(num, 10));
  return hours + (minutes || 0) / 60;
};

/**
 * Finds the applicable fasceSelezioneOre for a given date and time
 * If a time range spans multiple fasceSelezioneOre, returns the one containing the start time
 *
 * @param {Array} fasceSelezioneOre - Array of time selection rules from datiScuola
 * @param {string|moment} date - The date to check (YYYY-MM-DD format or moment object)
 * @param {string} startTime - Start time of the range (HH:mm format)
 * @param {string} endTime - End time of the range (HH:mm format) - optional
 * @returns {Object|null} The applicable rule object, or null if no rules apply
 */
export const findApplicableFascia = (
  fasceSelezioneOre,
  date,
  startTime,
  endTime = null
) => {
  if (
    !fasceSelezioneOre ||
    !Array.isArray(fasceSelezioneOre) ||
    fasceSelezioneOre.length === 0
  ) {
    return null;
  }

  const checkDate = moment(date);
  if (!checkDate.isValid()) {
    return null;
  }

  // Find all fasce that contain this date
  const applicableFasce = fasceSelezioneOre.filter((fascia) => {
    const dalDate = moment(fascia.dal);
    const alDate = moment(fascia.al);

    return (
      checkDate.isSameOrAfter(dalDate, "day") &&
      checkDate.isSameOrBefore(alDate, "day")
    );
  });

  if (applicableFasce.length === 0) {
    return null;
  }

  const getRestrictiveScore = (f) => {
    let score = 0;
    if (f.soloOreIntere) score += 100;
    if (f.tipoOrarioIntero === 1 || f.tipoOrarioIntero === 2) score += 10;
    if (f.permettiBucoMezzora === false) score += 1;
    return score;
  };

  // If we have a startTime, filter by time range
  if (startTime) {
    const startTimeHours = parseTimeToHours(startTime);

    let timeMatchingFasce;
    if (endTime) {
      const endTimeHours = parseTimeToHours(endTime);
      timeMatchingFasce = applicableFasce.filter((fascia) => {
        const fasciaStart = parseTimeToHours(fascia.dalle);
        const fasciaEnd = parseTimeToHours(fascia.alle);
        // Overlap condition: max(start1, start2) < min(end1, end2)
        return Math.max(startTimeHours, fasciaStart) < Math.min(endTimeHours, fasciaEnd);
      });
    } else {
      timeMatchingFasce = applicableFasce.filter((fascia) => {
        const fasciaStart = parseTimeToHours(fascia.dalle);
        const fasciaEnd = parseTimeToHours(fascia.alle);
        return startTimeHours >= fasciaStart && startTimeHours < fasciaEnd;
      });
    }

    if (timeMatchingFasce.length === 0) {
      return null;
    }

    // Sort to get the most restrictive one first
    timeMatchingFasce.sort((a, b) => getRestrictiveScore(b) - getRestrictiveScore(a));

    return timeMatchingFasce[0];
  }

  // If no time specified, sort all applicable fasce for the day by restrictiveness and return the first
  const sortedFasce = [...applicableFasce].sort((a, b) => getRestrictiveScore(b) - getRestrictiveScore(a));
  return sortedFasce[0];
};

/**
 * Checks if a duration (in hours) is valid according to soloOreIntere rule
 *
 * @param {number} duration - Duration in decimal hours (e.g., 1.5, 2.0)
 * @param {boolean} soloOreIntere - If true, only whole hours are allowed
 * @returns {boolean} True if duration is valid
 */
/**
 * Checks if a duration (in hours) is valid according to soloOreIntere rule
 *
 * @param {number} duration - Duration in decimal hours (e.g., 1.5, 2.0)
 * @param {boolean} soloOreIntere - If true, only whole hours are allowed
 * @param {number} tipoOrarioIntero - 0: free, 1: whole hours, 2: half hours, 3: multiples of minHours
 * @param {number} minOreAcquistabili - Minimum purchasable hours
 * @returns {boolean} True if duration is valid
 */
export const isValidDuration = (duration, soloOreIntere, tipoOrarioIntero = 0, minOreAcquistabili = 0) => {
  if (tipoOrarioIntero === 3 && minOreAcquistabili > 0) {
    const ratio = duration / minOreAcquistabili;
    return Math.abs(ratio - Math.round(ratio)) < 0.01;
  }

  if (!soloOreIntere) {
    return true; // No restriction
  }

  // Check if duration is a whole number (allowing for floating point precision)
  return Math.abs(duration - Math.round(duration)) < 0.01;
};

/**
 * Checks if a time (HH:mm) is valid according to tipoOrarioIntero rule
 *
 * @param {string} time - Time in HH:mm format
 * @param {number} tipoOrarioIntero - 0: free selection, 1: only whole hours, 2: only half hours, 3: multiples of minHours
 * @returns {boolean} True if time is valid
 */
export const isValidTimePoint = (time, tipoOrarioIntero) => {
  if (tipoOrarioIntero === 0 || tipoOrarioIntero === 3) {
    return true; // Free selection
  }

  const timeHours = parseTimeToHours(time);
  const minutes = (timeHours % 1) * 60;

  if (tipoOrarioIntero === 1) {
    // Only whole hours (00 minutes)
    return Math.abs(minutes) < 0.1;
  }

  if (tipoOrarioIntero === 2) {
    // Only half hours (30 minutes)
    return Math.abs(minutes - 30) < 0.1;
  }

  return false;
};

/**
 * Validates a time range against the applicable fasceSelezioneOre rules
 *
 * @param {Array} fasceSelezioneOre - Array of time selection rules
 * @param {string|moment} date - The date being checked
 * @param {string} startTime - Start time (HH:mm)
 * @param {string} endTime - End time (HH:mm)
 * @param {number} minOreAcquistabili - Minimum purchasable hours
 * @returns {Object} { isValid: boolean, reason: string }
 */
export const validateTimeRange = (
  fasceSelezioneOre,
  date,
  startTime,
  endTime,
  minOreAcquistabili = 0
) => {
  const fascia = findApplicableFascia(
    fasceSelezioneOre,
    date,
    startTime,
    endTime
  );

  if (!fascia) {
    return { isValid: true, reason: null }; // No rules apply
  }

  const { soloOreIntere, tipoOrarioIntero } = fascia;

  // Check start time validity
  if (!isValidTimePoint(startTime, tipoOrarioIntero)) {
    return {
      isValid: false,
      reason:
        tipoOrarioIntero === 1
          ? "start_must_be_whole_hour"
          : "start_must_be_half_hour",
    };
  }

  // Check end time validity
  if (!isValidTimePoint(endTime, tipoOrarioIntero)) {
    return {
      isValid: false,
      reason:
        tipoOrarioIntero === 1
          ? "end_must_be_whole_hour"
          : "end_must_be_half_hour",
    };
  }

  // Check duration validity
  const duration = parseTimeToHours(endTime) - parseTimeToHours(startTime);
  if (!isValidDuration(duration, soloOreIntere, tipoOrarioIntero, minOreAcquistabili)) {
    return {
      isValid: false,
      reason: tipoOrarioIntero === 3 ? "duration_must_be_multiple_of_min_hours" : "duration_must_be_whole_hours",
    };
  }

  return { isValid: true, reason: null };
};

/**
 * Gets the minimum allowed increment for time selection based on rules
 *
 * @param {Array} fasceSelezioneOre - Array of time selection rules
 * @param {string|moment} date - The date being checked
 * @param {string} time - The time point being checked
 * @returns {number} Increment in hours (0.5 for half-hour, 1.0 for whole hour)
 */
export const getMinimumIncrement = (fasceSelezioneOre, date, time) => {
  const fascia = findApplicableFascia(fasceSelezioneOre, date, time);

  if (!fascia) {
    return 0.5; // Default to half-hour increments
  }

  const { soloOreIntere } = fascia;

  // If only whole hours are allowed in duration, minimum is 1 hour
  if (soloOreIntere) {
    return 1.0;
  }

  // Otherwise allow half-hour increments
  return 0.5;
};
